import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ComponentManager from '@/Management/ComponentManager';
import { ICreatedInlineObjectsDTO, IInlineEditingContextDTO } from "coreframetypings/dto";
import CreateAndConnectLinkViewModel from '@/Components/Links/CreateAndConnectLink/CreateAndConnectLinkViewModel';
import Messenger from '@/Utility/Messenger';
import DetailViewExitedMessage from '@/Messages/DetailViewExitedMessage';
import { Dictionary } from '#/DefaultViewExtensionInterfaces';
import NavigationHelper from '@/Management/NavigationHelper';
import Utilities from '@/Utility/Utilities';
import AssociationNavigationMessage from '@/Messages/AssociationNavigationMessage';

export default class ExtIEGCreateAndConnectLinkViewModel extends CreateAndConnectLinkViewModel {

    private inlineEditingContext: KnockoutObservable<IInlineEditingContextDTO>;

    private static isRegistered: boolean;
    private static calls: Dictionary<ICreatedInlineObjectsDTO> = {};

    public constructor(protected params: any) {
        super(params);
        this.inlineEditingContext = this.observableOrDefault(params.inlineEditingContext, null);
    }

    /**
     * @override
     */
    protected create(pdType: string, contextPdoId: string, contextTypeName: string, contextRelationName: string): JQuery.Promise<boolean> {
        ExtIEGCreateAndConnectLinkViewModel.registerForMessages();

        return RADEnvironmentProxy.getInstance().createFromInlineEditing({
            inlineEditingContextId: this.inlineEditingContext().id
        }).then((result) => {
            return this.openObjectWithRegistration(result);
        }).promise();
    }

    /**
     * @override
     */
    protected createIsVisibleObservable(): KnockoutObservable<boolean> {
        if (!Utilities.isNullOrUndefined(this.params.isVisible)) {
            return this.observableOrDefault(this.params.isVisible);
        }

        this.setRequiredObservables();

        return this.createWritableComputed(() => !this.params.context?.isSummaryViewMode() && this.listView()?.showButtonCreate);
    }

    /**
     * Creates and opens a new object. Registers if the new object is discarded
     * @param createdObjects
     */
    private openObjectWithRegistration(createdObjects: ICreatedInlineObjectsDTO): boolean {
        if (Utilities.isNullOrUndefinedOrBlank(createdObjects.openObjectPDOID)) {
            if (this.navigateOnCreate)
                console.error("No object to open found.")

            return false;
        }

        const editingContext = this.inlineEditingContext();
        const contextId = 'FROM_INLINE_' + editingContext.id;
        ExtIEGCreateAndConnectLinkViewModel.calls[contextId] = createdObjects;
        Messenger.send(new AssociationNavigationMessage({
            fromPDTypeName: editingContext.contextObjectPdTypeName,
            fromPdoId: editingContext.contextObjectPdoId,
            toPDTypeName: createdObjects.openObjectPDTypeName,
            toPdoId: createdObjects.openObjectPDOID,
            relation: this.contextRelationName(),
        }));
        NavigationHelper.openDetailView('EDIT', createdObjects.openObjectPDTypeName, {
            pdoId: createdObjects.openObjectPDOID,
            contextId: contextId,
            // wird benötigt, damit im UID trotz "EDIT" das "NEW" angezeigt wird
            contextParameters: {
                'IS_CREATED_FROM': this.context.genericObject.pdoId
            }
        });

        return true;
    }

    /**
     * Registers when the detail view is closed and deletes discarded objects
     */
    private static registerForMessages(): void {
        if (ExtIEGCreateAndConnectLinkViewModel.isRegistered)
            return;

        ExtIEGCreateAndConnectLinkViewModel.isRegistered = true;

        Messenger.registerForMessageId('DetailViewExitedMessage', (message: DetailViewExitedMessage) => {
            if (message.context != null && message.context._callId in ExtIEGCreateAndConnectLinkViewModel.calls) {
                const objects = ExtIEGCreateAndConnectLinkViewModel.calls[message.context._callId];
                
                ExtIEGCreateAndConnectLinkViewModel.applyCallbackForCreatedObjects(message.hasObjectChanged ? 'confirm' : 'delete', objects);
                
                delete ExtIEGCreateAndConnectLinkViewModel.calls[message.context._callId];

                // unregister if there is no call left
                if (_.keys(ExtIEGCreateAndConnectLinkViewModel.calls).length == 0)
                    ExtIEGCreateAndConnectLinkViewModel.unregisterMessages();
            }
        }, ExtIEGCreateAndConnectLinkViewModel);
    }

    private static applyCallbackForCreatedObjects(callback: "confirm" | "delete", objects: ICreatedInlineObjectsDTO): void {
        const isConfirmed = callback == "confirm";
        _.keys(objects.createdObjects).forEach((typeName: string) => {
            if (ExtIEGCreateAndConnectLinkViewModel.isOnlyOpenedObject(objects, typeName)) {
                return;
            }
            RADEnvironmentProxy.getInstance().confirmInlineEditingObjects({
                pdTypeName: typeName,
                pdoIDs: objects.createdObjects[typeName],
                isConfirmed: isConfirmed,
            });
        });
    }

    private static isOnlyOpenedObject(objects: ICreatedInlineObjectsDTO, typeName): boolean {
        return typeName == objects.openObjectPDTypeName
            && objects.createdObjects[typeName].length == 1
            && objects.createdObjects[typeName].indexOf(objects.openObjectPDOID) > -1;
    }

    /**
     * Deletes the registrations on closing the detail view
     */
    private static unregisterMessages() {
        Messenger.unregisterForMessageId('DetailViewExitedMessage', null, ExtIEGCreateAndConnectLinkViewModel);
        ExtIEGCreateAndConnectLinkViewModel.isRegistered = false;
    }
}

ComponentManager.setViewModel('ExtIEGCreateAndConnectLink', ExtIEGCreateAndConnectLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');